import { FC, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import styled from 'styled-components/native';

import Icon7Eleven from '../../../assets/svg/Icon7Eleven';
import IconWarning from '../../../assets/svg/IconWarning';
import { peacock, warrior } from '../../../constants/color';
import Button, { ButtonType } from '../../../elements/CommonButton';
import Modal, { ModalProps } from '../../../elements/Modal';
import RadioButton from '../../../elements/RadioButton';
import { useTranslation } from '../../../i18n';
import { DeliveryMode } from '../../../types/Common';
import { DeliveryMethod } from '../../../types/DeliveryMethod';
import { getIsTW } from '../../../utils/countryInfo';
import { useData } from './hooks';

const DeliveryModal: FC<ModalProps> = props => {
  const { setIsOpen } = props;
  const { t } = useTranslation();
  const {
    methods,
    currency,
    shippingFees,
    putSubtotal,
    currentMethod,
    isFreeDelivery,
  } = useData();
  const [selectedMethod, setSelectedMethod] = useState<
    DeliveryMode | undefined
  >(currentMethod);

  useEffect(() => {
    if (currentMethod && currentMethod !== selectedMethod) {
      setSelectedMethod(currentMethod);
    }
  }, [currentMethod]);

  const onDeliveryModalButtonPress = () => {
    setIsOpen(false);

    putSubtotal({ selected_delivery_mode: selectedMethod! });
  };

  const renderLabelIcon = (method: DeliveryMethod) => {
    return method.id === DeliveryMode.CONVENIENCE_STORE_PICKUP ? (
      <Icon7Eleven size={24} />
    ) : null;
  };

  return (
    <Modal {...props}>
      <S.TitleWrapper>
        <S.Title>{t('deliveryMethods.title')}</S.Title>
      </S.TitleWrapper>
      {methods.map((method, index) => {
        // 目前只有超取會 disabled
        const disabled = method.cvs_delivery_errors?.length > 0;
        const isChecked = selectedMethod === method.id && !disabled;
        const errorMessage =
          method.id === DeliveryMode.CONVENIENCE_STORE_PICKUP
            ? method.cvs_delivery_errors?.[0]?.message
            : undefined;

        return (
          <View key={method.id}>
            <S.MethodWrapper testID={`DeliveryModal-Btn`}>
              <RadioButton
                value={method.id}
                label={method.text}
                labelFieldStyle={styles.selectionTextField}
                labelTextStyle={styles.selectionText}
                wrapperStyle={styles.wrapperStyle}
                labelIconEnd={renderLabelIcon(method)}
                checked={isChecked}
                disable={disabled}
                defaultChecked={isChecked}
                onPress={() => setSelectedMethod(method.id)}
              />
              <S.Price disabled={disabled}>
                {`${currency?.symbol} ${
                  isFreeDelivery
                    ? 0
                    : shippingFees.find(
                        shippingFee => shippingFee.delivery_mode === method.id
                      )?.price || ''
                }`}
              </S.Price>
            </S.MethodWrapper>
            {disabled && (
              <S.HintWrapper>
                <IconWarning size={20} fillColor={peacock} />
                <S.Hint>{errorMessage}</S.Hint>
              </S.HintWrapper>
            )}

            {index !== methods.length - 1 && <S.Separator />}
          </View>
        );
      })}
      <S.DescriptionSection>
        <S.Description>{t('delivery-modal-description-1')}</S.Description>
        {getIsTW() && (
          <S.Description>{t('delivery-modal-description-2')}</S.Description>
        )}
      </S.DescriptionSection>
      <Button
        buttonType={ButtonType.PRIMARY}
        disabled={!selectedMethod}
        isLoading={false}
        onPress={onDeliveryModalButtonPress}
        text={t('confirm')}
        testID="DeliveryModal-btn-confirm"
      />
    </Modal>
  );
};

const S = {
  MethodWrapper: styled.View`
    flex-direction: row;
    justify-content: center;
  `,
  Price: styled.Text<{ disabled: boolean }>`
    margin-top: 20px;
    justify-content: center;
    border: 1px solid transparent;
    color: ${({ theme, disabled }) =>
      disabled ? theme.palette.neutralGrey : theme.palette.warrior};
  `,
  TitleWrapper: styled.View`
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px;
  `,
  Title: styled.Text`
    margin-left: 6px;
    font-size: 18px;
    font-weight: 700;
    line-height: 25px;
    color: ${({ theme }) => theme.palette.warrior};
  `,
  DescriptionSection: styled.View`
    margin-top: 30px;
    margin-bottom: 30px;
  `,
  Description: styled.Text`
    font-size: 13px;
    color: ${({ theme }) => theme.palette.neutralDark};
    margin-top: 10px;
  `,
  Separator: styled.View`
    flex: 1;
    max-height: 1px;
    min-height: 1px;
    margin-top: 12px;
    padding: 0px;
    border-style: solid;
    border-top-width: 1px;
    border-color: ${({ theme }) => theme.palette.neutralLight};
  `,
  HintWrapper: styled.View`
    flex-direction: row;
    padding-left: 25px;
  `,
  Hint: styled.Text`
    color: ${({ theme }) => theme.palette.peacock};
    font-size: 13px;
    margin-left: 4px;
    margin-right: 10px;
  `,
};

const styles: any = StyleSheet.create({
  selectionTextField: {
    alignItems: 'center',
  },
  selectionText: {
    fontSize: 15,
    color: warrior,
    marginHorizontal: 8,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  wrapperStyle: {
    paddingLeft: 0,
    marginTop: 20,
  },
});

export default DeliveryModal;
